import type { Router } from 'vue-router'
import { App } from '@capacitor/app'
import type { URLOpenListenerEvent } from '@capacitor/app'
import { Mixpanel } from '@/analytics/mixpanel'
import { CapacitorAdjust } from '@/services/capacitor.adjustPlugin'
import { appStore } from '@/services/cordova.purchase'
import { notificationService, NotificationType } from '@/services/notification'

import { platform } from '@/services/platform'

import {
  ADJUST_ANDROID_TOKEN,
  ADJUST_IOS_TOKEN,
  IS_PRODUCTION,
} from '@/init/settings'
import { gotUrlEvent } from '@/app/router'
import { Sentry } from '@/services/sentry'
import { emitter } from '@/services/mitt'
import { Browser } from '@capacitor/browser'
import { playerSession } from '@/services/player'

type StartupEventLink = {
  event: string
  link: string
  notification?: NotificationType
}

export function init(router: Router): void {
  // The "deviceready" is sent by capacitor only when running in native app.
  document.addEventListener('deviceready', () => onDeviceReady(), false)

  let isDeviceReady = false
  let delayedLink: StartupEventLink | undefined

  // Enable Shortform links in browser to open in native app.
  // See: https://capacitorjs.com/docs/guides/deep-links
  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    const url = event.url
    let slug = ''

    // The in-app browser can't close itself with `Browser.close()`.
    // In those cases, we change the window position with a deeplink
    // and add this `?close-browser` query, which we can use as a signal
    // to call `Browser.close()` from within the app.
    if (url === 'shortform://?close-browser16218') {
      Browser.close()
      emitter.emit('closeInAppBrowser16218')
      return
    }

    if (url.startsWith('shortform://')) {
      // "shortform://app/books" => "/app/books"
      slug = url.replace('shortform://', '/')
    } else {
      // "https://www.shortform.com/app/books" => "/app/books"
      slug = url.replace('https://www.shortform.com', '')
    }

    // Special-case: /install inside app should open Discover page
    if (slug === '/install' || slug.startsWith('/install')) {
      slug = '/app'
    }

    // We only push to the route if there is a slug present
    if (slug) {
      handleStartupEventLink({ event: 'appUrlOpen', link: slug })
    }
  })

  // Accept taps in native push notifications.
  notificationService.startupRegisterListeners((notification, event, link) =>
    handleStartupEventLink({ notification, event, link }),
  )

  // Internal function which handles both 'appUrlOpen' and 'notificationActionPerformed'
  // events. Called by the event handlers above.
  function handleStartupEventLink(event: StartupEventLink): void {
    // If we have not yet restored the starting position, tell the
    // app not to. This URL will supersede that.
    // (If the app is already running, this has no effect.)
    gotUrlEvent()

    // Is it too early for router.push()?
    if (!isDeviceReady) {
      // The event arrived before deviceready. It's too early
      // to handle it, so we'll store it for later.
      if (delayedLink) {
        // ...But someone's already stored a slug. That shouldn't happen.
        Sentry.withScope((scope) => {
          scope.setExtra('First URL', delayedLink)
          scope.setExtra('Second URL', event.link)
          scope.setExtra('Second event type', event.event)
          Sentry.captureMessage('Two startup events with links')
        })
      } else {
        delayedLink = event
      }
    } else {
      // We can handle the event right now.
      openNativeLink(event)
      if (event.event === 'appUrlOpen') {
        Mixpanel.trackDeepLinkClick(event.link)
      } else {
        Mixpanel.trackNotificationOpen(
          event.notification,
          event.event,
          event.link,
        )
      }
    }
  }

  /**
   * Open the page specified by the given event.
   *
   * When a user taps a Shortform link handled by the operating system or taps
   * a push notification, the native callbacks provide the target URL but do
   * not update Vue Router, so this function routes the app to that URL.
   *
   * When a notification opens a guide, we record `notification` as the arrival
   * for its next `Audio Play` tracking event.
   */
  function openNativeLink(event: StartupEventLink): void {
    const targetRoute = router.resolve(event.link)
    const isNotificationToGuide =
      event.event !== 'appUrlOpen' && Boolean(targetRoute.meta.isDocContentPage)
    if (isNotificationToGuide) {
      playerSession.value.setNextArrival('notification')
    }
    router.push(event.link)
  }

  // We forward this event to the emitter because it allows us to
  // unsubscribe listeners more granularly (with `App`, we can only
  // unsubscribe all listeners at once).
  App.addListener('appStateChange', (state) => {
    emitter.emit('appVisibilityChange', state)
  })

  initAndroidBackButtonHandler(router)

  async function onDeviceReady(): Promise<void> {
    isDeviceReady = true

    if (delayedLink) {
      // Handle a delayed link event. Clear the delayedLink reference,
      // then send the Mixpanel event (with delayed=true).
      const event = delayedLink
      delayedLink = undefined
      openNativeLink(event)
      if (event.event === 'appUrlOpen') {
        Mixpanel.trackDeepLinkClick(event.link, true)
      } else {
        Mixpanel.trackNotificationOpen(
          event.notification,
          event.event,
          event.link,
          true,
        )
      }
    }

    const info = await platform.deviceInfo()
    if (Sentry.setContext) {
      Sentry.setContext('SF: NATIVE APP INFO', info as any)
    }

    // Inform In-App Purchase plugin that the device is ready.
    if (info.platform === 'ios' || info.platform === 'android') {
      initAdjust(info.platform)

      appStore.deviceReady()
    }
  }
}

/**
 * Initialize the Adjust plugin.
 */
function initAdjust(platform: string): void {
  CapacitorAdjust.init()
  const appToken = platform === 'ios' ? ADJUST_IOS_TOKEN : ADJUST_ANDROID_TOKEN
  const environment = IS_PRODUCTION ? 'production' : 'sandbox'
  CapacitorAdjust.initSdk({
    appToken,
    environment,
    logLevel: IS_PRODUCTION ? undefined : 'verbose',
  }).catch(Sentry.captureException)
}

/**
 * Configure behaviour of the back button of Android devices inside the app.
 *
 * The back button can be configured to close a menu instead of navigating to the
 * previous page. This is the common behaviour on Android. To do this, menus should emit
 * the event `overlayMenuOpened` when they get opened, and `overlayMenuClosed`.
 *
 * Once the Android back button gets clicked, if any menu is open as determined by
 * events, the event `closeOverlayMenu` gets emitted. Menus can react on this event
 * and close themselves.
 *
 * Currently, the menu on the home/discover page and the book TOC are set up to be
 * closed on Android back button click.
 *
 * If no menu is open, the current route is the 'home' route (i.e. /app), and the
 * browser navigation history is empty (i.e. already navigated all the way back), the
 * app gets closed.
 *
 * Otherwise, clicking the back button leads to a navigation to the previous
 * route of the browser history.
 */
function initAndroidBackButtonHandler(router: Router): void {
  let modalOpen: boolean = false

  emitter.on('overlayMenuOpened', () => {
    modalOpen = true
  })
  emitter.on('overlayMenuClosed', () => {
    modalOpen = false
  })

  App.addListener('backButton', ({ canGoBack }) => {
    if (modalOpen) {
      emitter.emit('closeOverlayMenu')
    } else if (canGoBack) {
      router.back()
    } else {
      if (router.currentRoute.value.name === 'discover') {
        App.exitApp()
      } else {
        // We do `replace` and not `push` here since we do
        // not want next "back" action to navigate again to
        // the previous page, creating a loop between
        // that page and home.
        // Next 'back' on the discover page should exit the app.
        router.replace({ name: 'discover' })
      }
    }
  })
}
